import { Grid } from '~/engine/data/common/Grid';
import type { RoiId } from '../changes/ids';
import { RoiReadModel } from '../readmodels/StructureSetReadModel';
import { ReversibleOperation, RoiMementoFactory } from './';
import { ContourStepMove, ContourStepRef } from './ContourEditStep';
import {
    BASELINE_STATE_INDEX,
    INITIAL_STATE_OPERATION,
    UndoRedoManager,
} from './UndoRedoManager';

export type ContourOperationResult = {
    compressedPixels: Uint8Array;
    grid: Grid;
    roiId: RoiId;
    structureSetUid: string;
};

/** A restored snapshot, with the step the ROI left and the step it is on now. */
export type ContourRestore = ContourOperationResult & {
    steps: ContourStepMove;
};

/** One retained step, as the history list renders it. */
export type ContourHistoryEntry = {
    /** Pass back to {@link ContourUndoService.jumpTo} to restore this step. */
    index: number;
    /** Raw operation label, e.g. 'margin' or {@link INITIAL_STATE_OPERATION}. */
    operation: string;
    timestamp: Date;
    /** The step the ROI's contour currently reflects. */
    isCurrent: boolean;
    /** The state before this session's first edit of the ROI. */
    isInitial: boolean;
};

/**
 * A restore produced by {@link ContourUndoService.jumpTo}. `direction` says
 * whether the target sits before or after the step the ROI was on, so the
 * caller can publish the matching undo/redo event; both restore pixels
 * identically, and the distinction exists for listeners that care which way
 * the user moved.
 */
export type ContourJump = {
    result: ContourRestore;
    direction: 'backward' | 'forward';
};

/**
 * Service for contour undo/redo operations using full pixel snapshots.
 * Stores complete compressed pixel data for each operation.
 */
export class ContourUndoService {
    private roiStacks = new Map<RoiId, UndoRedoManager>();
    private globalListeners = new Set<() => void>();

    private notifyGlobalListeners(): void {
        this.globalListeners.forEach((listener) => listener());
    }

    private getStack(roiId: RoiId): UndoRedoManager | undefined {
        return this.roiStacks.get(roiId);
    }

    private getOrCreateStack(roiId: RoiId): UndoRedoManager {
        if (!this.roiStacks.has(roiId)) {
            const stack = new UndoRedoManager();
            stack.subscribe(() => this.notifyGlobalListeners());
            this.roiStacks.set(roiId, stack);
        }
        return this.roiStacks.get(roiId)!;
    }

    private extractOperationResult(
        operation: ReversibleOperation
    ): ContourOperationResult | undefined {
        const state = operation.getState();
        if (!state.compressedContourPixels || !state.grid) {
            return undefined;
        }
        return {
            compressedPixels: state.compressedContourPixels,
            grid: state.grid,
            roiId: operation.getRoiId(),
            structureSetUid: operation.getStructureSetUid(),
        };
    }

    /**
     * Moves the stack's pointer with `move` and returns the restore it
     * produced, naming the step left and the step restored. Undefined when
     * `move` declines or the restored step holds no pixels.
     */
    private restore(
        stack: UndoRedoManager,
        move: () => ReversibleOperation | undefined
    ): ContourRestore | undefined {
        const from = stepOf(stack);
        const operation = move();
        if (!operation) {
            return undefined;
        }
        const result = this.extractOperationResult(operation);
        return result && { ...result, steps: { from, to: stepOf(stack) } };
    }

    /** The step the ROI's contour currently reflects; 'initial' for an ROI with no history. */
    currentStep(roiId: RoiId): ContourStepRef {
        const stack = this.getStack(roiId);
        return stack ? stepOf(stack) : 'initial';
    }

    /**
     * For React hook to subscribe to global undo/redo state changes
     */
    subscribe(listener: () => void): { unsubscribe: () => void } {
        this.globalListeners.add(listener);
        listener(); // Call immediately
        return {
            unsubscribe: () => {
                this.globalListeners.delete(listener);
            },
        };
    }

    /**
     * Add a contour operation to the ROI's undo stack
     * @param operation - Operation name, use 'initial-state' for the first capture
     */
    async addContourOperation(
        roiReadModel: RoiReadModel,
        pixels: Uint8Array[],
        grid: Grid,
        operation: string
    ): Promise<void> {
        const description =
            operation === INITIAL_STATE_OPERATION
                ? `Initial state for ROI ${roiReadModel.name}`
                : `${operation} on ROI ${roiReadModel.name}`;

        const memento = await RoiMementoFactory.fromReadModelWithContours(
            roiReadModel,
            pixels,
            grid
        );

        const reversibleOp = new ReversibleOperation(
            memento,
            description,
            operation
        );
        const stack = this.getOrCreateStack(roiReadModel.roiId);
        stack.addOperation(reversibleOp);
    }

    /**
     * Undo the last contour operation for a specific ROI
     */
    undo(roiId: RoiId): ContourRestore | undefined {
        const stack = this.getStack(roiId);
        return stack && this.restore(stack, () => stack.undo());
    }

    /**
     * Redo the last undone contour operation for a specific ROI
     */
    redo(roiId: RoiId): ContourRestore | undefined {
        const stack = this.getStack(roiId);
        return stack && this.restore(stack, () => stack.redo());
    }

    /**
     * The ROI's snapshot immediately before its most recent operation, without
     * disturbing the undo/redo pointer. Returns undefined when the ROI has no
     * prior state. Used to render a "what the last operation changed" diff
     * overlay against the live contour.
     */
    peekPrevious(roiId: RoiId): ContourOperationResult | undefined {
        const stack = this.getStack(roiId);
        if (!stack) {
            return undefined;
        }
        const operation = stack.peekPrevious();
        if (!operation) {
            return undefined;
        }
        return this.extractOperationResult(operation);
    }

    /**
     * Every retained step for an ROI, oldest first. Empty when the ROI has no
     * history. Read-only: it never moves the undo pointer.
     */
    getHistory(roiId: RoiId): ContourHistoryEntry[] {
        const stack = this.getStack(roiId);
        if (!stack) {
            return [];
        }
        return stack.getHistory().map((entry) => ({
            index: entry.index,
            operation: entry.operation.getOperation(),
            timestamp: entry.operation.getTimestamp(),
            isCurrent: entry.isCurrent,
            isInitial: entry.isBaseline,
        }));
    }

    /**
     * How many intermediate steps were dropped to keep the ROI's stack within
     * its size cap. Non-zero means {@link getHistory} has a gap between the
     * initial state and the oldest retained edit, and the UI must say so
     * rather than present the list as complete.
     */
    getDiscardedStepCount(roiId: RoiId): number {
        return this.getStack(roiId)?.discardedStepCount ?? 0;
    }

    /**
     * Restore an ROI to an arbitrary retained step. Returns undefined when the
     * index is not in the ROI's history or the ROI is already on it, so a
     * no-op jump publishes no restore event.
     */
    jumpTo(roiId: RoiId, index: number): ContourJump | undefined {
        const stack = this.getStack(roiId);
        if (!stack) {
            return undefined;
        }

        const from = stack.currentStateIndex;
        if (index === from) {
            return undefined;
        }

        const result = this.restore(stack, () => stack.jumpToState(index));
        if (!result) {
            return undefined;
        }
        return { result, direction: index < from ? 'backward' : 'forward' };
    }

    /**
     * Restore an ROI to the state it was in before this session's first edit.
     * The steps jumped over stay redoable until the next edit is recorded, so
     * this is recoverable.
     *
     * Declines when the stack holds no baseline: index 0 is then an ordinary
     * edit, and restoring it under this name would present a half-edited
     * contour as the original.
     */
    revertToInitial(roiId: RoiId): ContourJump | undefined {
        if (!this.getStack(roiId)?.hasBaseline) {
            return undefined;
        }
        return this.jumpTo(roiId, BASELINE_STATE_INDEX);
    }

    /** True when the ROI has edits to revert, i.e. it is not already initial. */
    canRevertToInitial(roiId: RoiId): boolean {
        const stack = this.getStack(roiId);
        return (
            !!stack &&
            stack.hasBaseline &&
            stack.currentStateIndex > BASELINE_STATE_INDEX
        );
    }

    canUndo(roiId: RoiId): boolean {
        const stack = this.getStack(roiId);
        return stack ? stack.canUndo() : false;
    }

    canRedo(roiId: RoiId): boolean {
        const stack = this.getStack(roiId);
        return stack ? stack.canRedo() : false;
    }

    /**
     * Drops the undo/redo history of a deleted ROI, whose snapshots would
     * otherwise stay in memory for the rest of the session.
     */
    clearRoi(roiId: RoiId): void {
        const stack = this.roiStacks.get(roiId);
        if (!stack) {
            return;
        }
        stack.clear();
        this.roiStacks.delete(roiId);
        this.notifyGlobalListeners();
    }

    clear(): void {
        this.roiStacks.forEach((stack) => stack.clear());
        this.roiStacks.clear();
        this.notifyGlobalListeners();
    }

    isRoiInitialized(roiId: RoiId): boolean {
        const stack = this.getStack(roiId);
        return stack ? stack.size > 0 : false;
    }
}

/** The step a stack's pointer is on; the baseline is 'initial'. */
function stepOf(stack: UndoRedoManager): ContourStepRef {
    const current = stack.getHistory().find((entry) => entry.isCurrent);
    if (!current || current.isBaseline) {
        return 'initial';
    }
    return {
        operation: current.operation.getOperation(),
        recordedAtMs: current.operation.getTimestamp().getTime(),
    };
}
